import type {
  ClientToServerEvents,
  ServerToClientEvents
} from '@shared/all/domains/socket';
import { LAZY_CHAT_ROOMS } from '@shared/all/domains/socket';
import { resolveDevApiUrl } from '@shared/all/helpers/devApiUrl';
import { io, type Socket } from 'socket.io-client';

export type TypedSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

// In dev, force the socket URL hostname to match the browser's hostname so the
// connection stays same-origin and cookies aren't blocked by SameSite. Shares
// resolveDevApiUrl with the tRPC client so the two can never disagree about
// which host the API is on.
function resolveSocketUrl(): string {
  const envUrl = process.env.NEXT_PUBLIC_API_URL!;
  if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
    return resolveDevApiUrl(envUrl, window.location.hostname);
  }
  return envUrl;
}

const socket: TypedSocket = io(resolveSocketUrl(), {
  withCredentials: true,
  autoConnect: false,
  // This client joins channel rooms itself (./conversationRooms.ts). Without
  // it the server joins every conversation at connect, as old clients expect.
  query: { chatRooms: LAZY_CHAT_ROOMS }
});

// A short-lived, UNauthenticated socket for the push-approval login: it carries
// only the pendingLoginId so the server can wake this browser (`loginApproved`)
// the moment the phone approves. Disconnect it once login completes/cancels.
export function createPendingLoginSocket(pendingLoginId: string): TypedSocket {
  return io(resolveSocketUrl(), {
    withCredentials: true,
    auth: { pendingLoginId }
  });
}

// Dev-only: expose the socket on window so we can poke at it from devtools
// without juggling chunked next.js modules.
if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
  (window as unknown as { socket: TypedSocket }).socket = socket;
}

export default socket;
